/**
 * Settings Redux actions — slice dédié à la page Paramètres / onglet Intégrations.
 * Généralisé par type d'intégration ("prospect" en phase 1, "emcf" plus tard).
 * Thunks : getIntegration, saveIntegration, testIntegration, syncIntegration,
 * fetchIntegrationBoxes, saveIntegrationBoxes (liste blanche des box envoyées),
 * fetchIntegrationLogs (journal des envois) ; downloadIntegrationLogCsv hors Redux.
 * Aucun secret n'est jamais stocké dans le state Redux (le backend ne les renvoie pas,
 * seulement `secretsMasked`).
 */

/**
 * Lecture défensive du corps de réponse.
 * `response.json()` jette « Unexpected end of JSON input » sur un corps vide, et
 * « Unexpected token » sur du texte (un 404/403 Express peut renvoyer du texte brut).
 * On lit le texte puis on parse seulement si c'est du JSON ; sinon on remonte le texte
 * brut comme { error } pour un message clair.
 */
async function readResponseBody(response) {
    const text = await response.text();
    if (!text) {
        return {};
    }
    try {
        return JSON.parse(text);
    } catch (e) {
        return { error: text };
    }
}

/**
 * Message + code HTTP pour une réponse en erreur. Le code est remonté jusqu'au composant
 * (ex. 503 = environnement non autorisé à envoyer vers Prospect) pour un rendu différent
 * (bandeau d'information plutôt qu'un message d'erreur technique).
 */
function errorInfoFor(response, body, fallback) {
    if (response.status === 403) {
        return { message: 'Accès refusé (page Intégrations)', code: response.status };
    }
    return { message: body.error || fallback, code: response.status };
}

/**
 * Charger l'état d'une intégration pour le compte courant.
 * Résultat : { type, configured, enabled, settings, secretsMasked, updatedAt, updatedBy, sync?,
 *              outboundEnabled?, simulation? }
 * outboundEnabled = état de l'environnement (envois autorisés) ; simulation = mode effectif du
 * compte (!outboundEnabled || settings.simulation=="true") ; les deux absents sur un ancien
 * domainModel qui ne les renvoie pas encore.
 * sync (absent si aucune synchro n'a jamais été lancée) :
 * { running, trigger?, triggeredBy?, lastStartedAt?, lastFinishedAt?, lastStatus, lastError?,
 *   categories: { agents: R, customers: R, installations: R, payments_ts: R }, cursors?: { payments_ts } }
 */
export function getIntegration(type) {
    return async function (dispatch) {
        dispatch({ type: 'SETTINGS_GET_INTEGRATION_START', payload: { integrationType: type } });
        try {
            const response = await fetch('/integrations/' + encodeURIComponent(type), {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
            });
            const body = await readResponseBody(response);
            if (!response.ok) {
                const info = errorInfoFor(response, body, 'Erreur chargement de l\'intégration');
                dispatch({
                    type: 'SETTINGS_GET_INTEGRATION_ERROR',
                    payload: { integrationType: type, error: info.message, errorCode: info.code },
                });
                return;
            }
            dispatch({
                type: 'SETTINGS_GET_INTEGRATION_SUCCESS',
                payload: {
                    integrationType: type,
                    configured: body.configured,
                    enabled: body.enabled,
                    settings: body.settings || {},
                    secretsMasked: body.secretsMasked || {},
                    updatedAt: body.updatedAt || null,
                    updatedBy: body.updatedBy || null,
                    sync: body.sync || null,
                    // outboundEnabled = état de l'environnement (envois autorisés ou non) ; simulation =
                    // mode effectif du compte. Absents sur un ancien domainModel : restent undefined.
                    outboundEnabled: body.outboundEnabled,
                    simulation: body.simulation,
                },
            });
        } catch (err) {
            dispatch({
                type: 'SETTINGS_GET_INTEGRATION_ERROR',
                payload: { integrationType: type, error: err.message || 'Erreur réseau' },
            });
        }
    };
}

/**
 * Enregistrer une intégration (PUT).
 * payload : { enabled, settings: {...}, secrets: { <categorie>: <valeur ou vide = inchangé> },
 *             clearSecrets?: ["agents"|"customers"|"installations"|"payments_ts"] }
 * Un secret ne doit jamais être à la fois dans `secrets` et dans `clearSecrets`.
 */
export function saveIntegration(type, payload) {
    return async function (dispatch) {
        dispatch({ type: 'SETTINGS_SAVE_INTEGRATION_START', payload: { integrationType: type } });
        try {
            const response = await fetch('/integrations/' + encodeURIComponent(type), {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify(payload),
            });
            const body = await readResponseBody(response);
            if (!response.ok) {
                const info = errorInfoFor(response, body, 'Erreur enregistrement de l\'intégration');
                dispatch({
                    type: 'SETTINGS_SAVE_INTEGRATION_ERROR',
                    payload: { integrationType: type, error: info.message, errorCode: info.code },
                });
                return;
            }
            dispatch({
                type: 'SETTINGS_SAVE_INTEGRATION_SUCCESS',
                payload: {
                    integrationType: type,
                    enabled: body.enabled !== undefined ? body.enabled : payload.enabled,
                    settings: body.settings || payload.settings || {},
                    secretsMasked: body.secretsMasked || {},
                    configured: body.configured !== undefined ? body.configured : true,
                    outboundEnabled: body.outboundEnabled,
                    simulation: body.simulation,
                },
            });
        } catch (err) {
            dispatch({
                type: 'SETTINGS_SAVE_INTEGRATION_ERROR',
                payload: { integrationType: type, error: err.message || 'Erreur réseau' },
            });
        }
    };
}

/**
 * Tester l'envoi d'un échantillon sans persister (POST .../test), avec les secrets déjà enregistrés.
 * Réponse serveur (toujours HTTP 200 en cas de succès) :
 * { status: "success"|"partial"|"error", categories: { agents: R, customers: R, installations: R, payments_ts: R } }
 * où R = { status: "ok"|"demo"|"simulation"|"error"|"skipped"|"partial", httpCode?, sent, batches, importIds?, demo?,
 *          message?, excluded?: {motif: nombre}, reverted?, recordedFrom?, since?, at }
 * ("partial" possible au global aussi quand une catégorie est "skipped" faute de secret configuré)
 * Erreurs : 409 = un test est déjà en cours pour ce compte. Un domainModel récent bascule en
 * simulation (status "simulation", pas d'erreur) quand l'envoi est désactivé sur cet environnement ;
 * le 503 « envoi désactivé » ne vient que d'un domainModel antérieur à ce mode (gestion conservée).
 */
export function testIntegration(type) {
    return async function (dispatch) {
        dispatch({ type: 'SETTINGS_TEST_INTEGRATION_START', payload: { integrationType: type } });
        try {
            const response = await fetch('/integrations/' + encodeURIComponent(type) + '/test', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
            });
            const body = await readResponseBody(response);
            if (!response.ok) {
                const info = errorInfoFor(response, body, 'Erreur test de l\'intégration');
                dispatch({
                    type: 'SETTINGS_TEST_INTEGRATION_ERROR',
                    payload: { integrationType: type, error: info.message, errorCode: info.code },
                });
                return;
            }
            dispatch({
                type: 'SETTINGS_TEST_INTEGRATION_SUCCESS',
                payload: {
                    integrationType: type,
                    status: body.status || null,
                    categories: body.categories || {},
                },
            });
        } catch (err) {
            dispatch({
                type: 'SETTINGS_TEST_INTEGRATION_ERROR',
                payload: { integrationType: type, error: err.message || 'Erreur réseau' },
            });
        }
    };
}

/**
 * Déclencher une synchronisation complète en arrière-plan.
 * Réponse : 202 { status: "started" } ; 400 si non configuré ou date de départ des paiements manquante ;
 * 409 si désactivé ou synchro déjà en cours. Un domainModel récent bascule en simulation (journal et
 * CSV, rien n'est envoyé) quand l'envoi vers Prospect est désactivé sur cet environnement ; le 503
 * ne vient que d'un domainModel antérieur à ce mode (gestion conservée).
 * Ne renvoie pas d'état détaillé : l'appelant doit rafraîchir via getIntegration (le GET expose `sync.running`).
 */
export function syncIntegration(type) {
    return async function (dispatch) {
        dispatch({ type: 'SETTINGS_SYNC_INTEGRATION_START', payload: { integrationType: type } });
        try {
            const response = await fetch('/integrations/' + encodeURIComponent(type) + '/sync', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
            });
            const body = await readResponseBody(response);
            if (!response.ok) {
                const info = errorInfoFor(response, body, 'Erreur déclenchement de la synchronisation');
                dispatch({
                    type: 'SETTINGS_SYNC_INTEGRATION_ERROR',
                    payload: { integrationType: type, error: info.message, errorCode: info.code },
                });
                return;
            }
            dispatch({
                type: 'SETTINGS_SYNC_INTEGRATION_SUCCESS',
                payload: {
                    integrationType: type,
                    message: body.status === 'started' ? 'Synchronisation démarrée' : (body.message || 'Synchronisation déclenchée'),
                },
            });
        } catch (err) {
            dispatch({
                type: 'SETTINGS_SYNC_INTEGRATION_ERROR',
                payload: { integrationType: type, error: err.message || 'Erreur réseau' },
            });
        }
    };
}

/**
 * Charger la liste blanche des associations box/client envoyées à l'intégration.
 * Réponse : { boxAssoIds: [...], synced: { boxAssoId: date du dernier envoi accepté (201) }, updatedAt, updatedBy }
 * (une association peut être dans `synced` sans être — ou ne plus être — dans `boxAssoIds`).
 * excluded : { boxAssoId: motif } des box sélectionnées écartées à la dernière synchro réelle
 * (motif en français, affiché tel quel ; une box peut être à la fois dans `synced` et `excluded`).
 * domainModel refuse le RBAC en 404 (« No Access ») et un accès suspendu en 403 : les deux sont
 * remontés en `forbidden`. Toute réponse en erreur, ou un 200 sans tableau `boxAssoIds`, laisse la
 * sélection non chargée : la page Home masque alors simplement sa colonne, sans afficher d'erreur.
 */
export function fetchIntegrationBoxes(type) {
    return async function (dispatch) {
        dispatch({ type: 'SETTINGS_GET_INTEGRATION_BOXES_START', payload: { integrationType: type } });
        try {
            const response = await fetch('/integrations/' + encodeURIComponent(type) + '/boxes', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
            });
            const body = await readResponseBody(response);
            if (!response.ok) {
                const info = errorInfoFor(response, body, 'Erreur chargement des box de l\'intégration');
                dispatch({
                    type: 'SETTINGS_GET_INTEGRATION_BOXES_ERROR',
                    payload: {
                        integrationType: type,
                        error: info.message,
                        forbidden: response.status === 403 || response.status === 404,
                    },
                });
                return;
            }
            // Un corps vide ou sans liste ne vaut pas « aucune box sélectionnée » : sinon la page Home
            // afficherait « Non » partout sur une réponse inattendue.
            if (!Array.isArray(body.boxAssoIds)) {
                dispatch({
                    type: 'SETTINGS_GET_INTEGRATION_BOXES_ERROR',
                    payload: { integrationType: type, error: 'Réponse inattendue du serveur (liste des box absente)' },
                });
                return;
            }
            dispatch({
                type: 'SETTINGS_GET_INTEGRATION_BOXES_SUCCESS',
                payload: {
                    integrationType: type,
                    selected: body.boxAssoIds,
                    synced: body.synced || {},
                    excluded: body.excluded || {},
                    updatedAt: body.updatedAt || null,
                    updatedBy: body.updatedBy || null,
                },
            });
        } catch (err) {
            dispatch({
                type: 'SETTINGS_GET_INTEGRATION_BOXES_ERROR',
                payload: { integrationType: type, error: err.message || 'Erreur réseau' },
            });
        }
    };
}

/**
 * Remplacer la liste blanche (PUT, liste complète). Réponse : même vue que le GET + `cursorsReset`
 * (true si des box ont été ajoutées : tout l'historique de leurs paiements sera renvoyé).
 * Erreurs (400 liste invalide, 409 enregistrement concurrent...) remontées dans `saveError`.
 * Retourne { ok, cursorsReset } pour que l'appelant affiche l'avertissement adéquat.
 */
export function saveIntegrationBoxes(type, boxAssoIds) {
    return async function (dispatch) {
        dispatch({ type: 'SETTINGS_SAVE_INTEGRATION_BOXES_START', payload: { integrationType: type } });
        try {
            const response = await fetch('/integrations/' + encodeURIComponent(type) + '/boxes', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ boxAssoIds }),
            });
            const body = await readResponseBody(response);
            if (!response.ok) {
                const info = errorInfoFor(response, body, 'Erreur enregistrement des box de l\'intégration');
                dispatch({
                    type: 'SETTINGS_SAVE_INTEGRATION_BOXES_ERROR',
                    payload: { integrationType: type, error: info.message },
                });
                return { ok: false, cursorsReset: false };
            }
            dispatch({
                type: 'SETTINGS_SAVE_INTEGRATION_BOXES_SUCCESS',
                payload: {
                    integrationType: type,
                    selected: body.boxAssoIds || boxAssoIds,
                    synced: body.synced || {},
                    excluded: body.excluded || {},
                    updatedAt: body.updatedAt || null,
                    updatedBy: body.updatedBy || null,
                },
            });
            return { ok: true, cursorsReset: !!body.cursorsReset };
        } catch (err) {
            dispatch({
                type: 'SETTINGS_SAVE_INTEGRATION_BOXES_ERROR',
                payload: { integrationType: type, error: err.message || 'Erreur réseau' },
            });
            return { ok: false, cursorsReset: false };
        }
    };
}

/**
 * Effacer l'erreur du dernier enregistrement de la sélection (à l'ouverture du modal),
 * pour ne pas réafficher une erreur d'une session d'édition précédente.
 */
export function resetIntegrationBoxesSaveError(type) {
    return { type: 'SETTINGS_RESET_INTEGRATION_BOXES_SAVE_ERROR', payload: { integrationType: type } };
}

/**
 * Charger le journal des envois (une entrée par paquet envoyé, du plus récent au plus ancien).
 * Réponse : { logs: [{ id, runId, trigger, triggeredBy, category, at, status, httpCode, sent, batches,
 *   importIds, demo, message, recordCount }], nextBefore } — nextBefore absent/vide = plus rien d'ancien.
 * options.before : curseur de pagination (nextBefore précédent) ; options.append : ajouter à la liste
 * déjà chargée (« Plus ancien ») au lieu de la remplacer. 403/404 → `logsForbidden` (section masquée).
 *
 * `seq` départage un rechargement automatique (reloadKey, fin de synchro) parti pendant un
 * « Plus ancien » en cours : incrémenté ici à chaque START et repassé au reducer, qui n'applique
 * SUCCESS/FAILURE que si sa `seq` est toujours la dernière lancée (sinon réponse en retard ignorée).
 */
export function fetchIntegrationLogs(type, { before, append } = {}) {
    return async function (dispatch, getState) {
        const settings = getState().settings;
        const current = settings && settings.integrations && settings.integrations[type];
        const seq = ((current && current.logsRequestSeq) || 0) + 1;
        dispatch({ type: 'SETTINGS_GET_INTEGRATION_LOGS_START', payload: { integrationType: type, seq } });
        try {
            const query = before ? '?before=' + encodeURIComponent(before) : '';
            const response = await fetch('/integrations/' + encodeURIComponent(type) + '/logs' + query, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
            });
            const body = await readResponseBody(response);
            if (!response.ok) {
                const info = errorInfoFor(response, body, 'Erreur chargement du journal des envois');
                dispatch({
                    type: 'SETTINGS_GET_INTEGRATION_LOGS_ERROR',
                    payload: {
                        integrationType: type,
                        error: info.message,
                        forbidden: response.status === 403 || response.status === 404,
                        seq,
                    },
                });
                return;
            }
            dispatch({
                type: 'SETTINGS_GET_INTEGRATION_LOGS_SUCCESS',
                payload: {
                    integrationType: type,
                    logs: Array.isArray(body.logs) ? body.logs : [],
                    nextBefore: body.nextBefore || null,
                    append: !!append,
                    seq,
                },
            });
        } catch (err) {
            dispatch({
                type: 'SETTINGS_GET_INTEGRATION_LOGS_ERROR',
                payload: { integrationType: type, error: err.message || 'Erreur réseau', seq },
            });
        }
    };
}

/**
 * Nom de fichier d'un en-tête Content-Disposition (filename*=UTF-8''... prioritaire, puis filename="...").
 */
function filenameFromDisposition(disposition) {
    if (!disposition) {
        return null;
    }
    const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(disposition);
    if (star) {
        try {
            return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
        } catch (e) {
            // encodage invalide : on tente le paramètre filename simple
        }
    }
    const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(disposition);
    if (plain) {
        return (plain[2] !== undefined ? plain[2] : plain[1]).trim();
    }
    return null;
}

/**
 * Télécharger le CSV des enregistrements envoyés dans une entrée du journal (hors Redux).
 * Le nom vient de Content-Disposition, repli prospect_<catégorie>_<date>.csv.
 * Retourne { ok, error } pour que la ligne affiche son état.
 */
export async function downloadIntegrationLogCsv(type, log) {
    try {
        const response = await fetch(
            '/integrations/' + encodeURIComponent(type) + '/logs/' + encodeURIComponent(log.id) + '/csv',
            { method: 'GET', credentials: 'same-origin' }
        );
        if (!response.ok) {
            const body = await readResponseBody(response);
            const info = errorInfoFor(response, body, 'Erreur téléchargement du CSV');
            return { ok: false, error: response.status === 404 ? 'CSV introuvable' : info.message };
        }
        const blob = await response.blob();
        const fallback = type + '_' + (log.category || 'envoi') + '_' + String(log.at || '').slice(0, 10) + '.csv';
        const filename = filenameFromDisposition(response.headers.get('Content-Disposition')) || fallback;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        // Révocation différée : certains navigateurs lisent l'URL après le click synchrone.
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        return { ok: true, error: null };
    } catch (err) {
        return { ok: false, error: err.message || 'Erreur réseau' };
    }
}

/**
 * Contrôle des données BGFA (hors Redux : rien n'est conservé dans le state, les compteurs
 * ne doivent jamais survivre à une erreur). Aucun envoi à Prospect, lecture seule.
 * Succès : { ok: true, data: {generatedAt, scope, counts, discarded, items} }.
 * Échec : { ok: false, status, code, error } — `code` = not_enabled | in_progress | load_failed
 * | timeout selon le corps renvoyé ; jamais de donnée partielle.
 */
export async function fetchIntegrationProblems(type, scope) {
    try {
        const response = await fetch(
            '/integrations/' + encodeURIComponent(type) + '/problems?scope=' + encodeURIComponent(scope || 'all'),
            { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', cache: 'no-store' }
        );
        const body = await readResponseBody(response);
        if (!response.ok) {
            const info = errorInfoFor(response, body, 'Contrôle impossible');
            return { ok: false, status: response.status, code: body.code || null, error: info.message };
        }
        // Un 2xx n'est un rapport que s'il en a la forme : jamais « aucun manque » sur une réponse
        // vide ou inattendue (proxy, ancien backend).
        if (!body || typeof body.counts !== 'object' || body.counts === null || Array.isArray(body.counts) || !Array.isArray(body.items)) {
            return { ok: false, status: response.status, code: null, error: 'Réponse inattendue du serveur' };
        }
        return { ok: true, data: body };
    } catch (err) {
        return { ok: false, status: 0, code: null, error: err.message || 'Erreur réseau' };
    }
}

/**
 * Télécharger le CSV du contrôle des données BGFA (liste complète du périmètre choisi).
 * Retourne { ok, status, code, error }.
 */
export async function downloadIntegrationProblemsCsv(type, scope) {
    try {
        const response = await fetch(
            '/integrations/' + encodeURIComponent(type) + '/problems?scope=' + encodeURIComponent(scope || 'all') + '&format=csv',
            { method: 'GET', credentials: 'same-origin', cache: 'no-store' }
        );
        if (!response.ok) {
            const body = await readResponseBody(response);
            const info = errorInfoFor(response, body, 'Erreur téléchargement du CSV');
            return { ok: false, status: response.status, code: body.code || null, error: info.message };
        }
        const blob = await response.blob();
        const filename = filenameFromDisposition(response.headers.get('Content-Disposition'))
            || type + '_donnees_bgfa_' + new Date().toISOString().slice(0, 10) + '.csv';

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        return { ok: true, status: 200, code: null, error: null };
    } catch (err) {
        return { ok: false, status: 0, code: null, error: err.message || 'Erreur réseau' };
    }
}

/**
 * Boutiques e-commerce (PrestaShop) : jumelage, relayé par le BFF /ecom vers storehouse
 * (type d'intégration 'ecom' du slice). Le droit (rôles 2 et 5) est tranché par storehouse.
 * Le code de jumelage n'entre JAMAIS dans le state Redux : les thunks de création et de
 * régénération le rendent à l'appelant, qui le garde en état local le temps de l'affichage.
 * Résultat des thunks d'écriture : { ok: true, data } ou { ok: false, status, body }.
 */
const ECOM_TYPE = 'ecom';

async function ecomRequest(method, path, payload) {
    const response = await fetch('/ecom' + path, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: payload === undefined ? undefined : JSON.stringify(payload),
    });
    const body = await readResponseBody(response);
    return { response, body };
}

export function fetchEcomLinks() {
    return async function (dispatch) {
        dispatch({ type: 'SETTINGS_ECOM_LINKS_START', payload: { integrationType: ECOM_TYPE } });
        try {
            const { response, body } = await ecomRequest('GET', '/links');
            if (!response.ok) {
                dispatch({
                    type: 'SETTINGS_ECOM_LINKS_ERROR',
                    payload: { integrationType: ECOM_TYPE, status: response.status, body },
                });
                return;
            }
            dispatch({ type: 'SETTINGS_ECOM_LINKS_SUCCESS', payload: { integrationType: ECOM_TYPE, body } });
        } catch (err) {
            dispatch({
                type: 'SETTINGS_ECOM_LINKS_ERROR',
                payload: { integrationType: ECOM_TYPE, status: 0, body: { error: err.message || 'Erreur réseau' } },
            });
        }
    };
}

// Écriture : START/END pour le drapeau `writing`, puis rechargement de la liste si succès.
function ecomWrite(method, path, payload) {
    return async function (dispatch) {
        dispatch({ type: 'SETTINGS_ECOM_WRITE_START', payload: { integrationType: ECOM_TYPE } });
        let result;
        try {
            const { response, body } = await ecomRequest(method, path, payload);
            result = response.ok
                ? { ok: true, data: body }
                : { ok: false, status: response.status, body };
        } catch (err) {
            result = { ok: false, status: 0, body: { error: err.message || 'Erreur réseau' } };
        }
        dispatch({ type: 'SETTINGS_ECOM_WRITE_END', payload: { integrationType: ECOM_TYPE } });
        if (result.ok) {
            dispatch(fetchEcomLinks());
        }
        return result;
    };
}

// legacyStoreId : rôle 5 seulement (refusé par storehouse sinon).
export function createEcomLink(shopRef, legacyStoreId) {
    const payload = { shop_ref: shopRef };
    if (legacyStoreId) {
        payload.legacy_store_id = legacyStoreId;
    }
    return ecomWrite('POST', '/links', payload);
}

export function regenerateEcomLinkCode(storeId) {
    return ecomWrite('POST', '/links/' + encodeURIComponent(storeId) + '/code', {});
}

export function detachEcomLink(storeId) {
    return ecomWrite('DELETE', '/links/' + encodeURIComponent(storeId));
}
